import { useState, useCallback, useMemo, useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { Group, UseManageGroupsProps, GroupAssignment } from './types';
import { useFilteredSelectionModel } from './gridStateHelper';

/**
 * Provides the standard grouping column definition for data grids
 * with tree grouping enabled. This configuration ensures consistent appearance
 * and behavior across all pages that use grouping functionality.
 *
 * @param isGroupingEnabled - Whether grouping is currently enabled
 * @returns The grouping column definition or undefined if grouping is disabled
 */
export const useGroupingColDef = (
  isGroupingEnabled: boolean
): GridColDef | undefined => {
  if (!isGroupingEnabled) {
    return undefined;
  }

  return {
    headerName: '',
    width: 30,
    hideDescendantCount: true,
    sortable: false,
    filterable: false,
    resizable: false,
    disableColumnMenu: true,
    pinnable: false,
    hideable: false,
    valueGetter: () => '',
  };
};

export const useManageGroups = ({
  availableRows,
  existingGroups = [],
}: UseManageGroupsProps) => {
  const [groups, setGroups] = useState<Group[]>(existingGroups);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  // Reinitialize groups when existingGroups changes (e.g., when modal reopens)
  useEffect(() => {
    setGroups(existingGroups);
  }, [existingGroups]);

  // Generate unique group ID
  const generateGroupId = useCallback(() => {
    return `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Validate group name for duplicates
  const validateGroupName = useCallback(
    (name: string, excludeGroupId?: string) => {
      if (!name.trim()) {
        return { isValid: false, error: 'Group name cannot be empty' };
      }

      const isDuplicate = groups.some(group => {
        if (excludeGroupId && group.id === excludeGroupId) {
          return false;
        }
        return group.name.toLowerCase() === name.trim().toLowerCase();
      });

      if (isDuplicate) {
        return {
          isValid: false,
          error: 'A group with this name already exists',
        };
      }

      return { isValid: true, error: '' };
    },
    [groups]
  );

  // Toggle row selection
  const toggleRowSelection = useCallback((rowId: string) => {
    setSelectedRowIds(prev =>
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  }, []);

  // Clear row selection
  const clearSelection = useCallback(() => {
    setSelectedRowIds([]);
    setActiveGroupId(null);
  }, []);

  // Handle group click to select its rows
  const handleGroupClick = useCallback(
    (groupId: string, selectedRowIdsRef?: React.MutableRefObject<string[]>) => {
      const group = groups.find(g => g.id === groupId);
      if (!group) return;

      // Toggle selection if clicking the same group
      if (activeGroupId === groupId) {
        setSelectedRowIds([]);
        setActiveGroupId(null);
        if (selectedRowIdsRef) {
          selectedRowIdsRef.current = [];
        }
      } else {
        // Validate that all group row IDs exist in available rows
        const validRowIds = new Set(availableRows.map(row => row.id));
        const validGroupRowIds = group.rowIds.filter(id => validRowIds.has(id));

        setSelectedRowIds(validGroupRowIds);
        setActiveGroupId(groupId);
        if (selectedRowIdsRef) {
          selectedRowIdsRef.current = validGroupRowIds;
        }
      }
    },
    [groups, activeGroupId, availableRows]
  );

  // Create new group
  const createGroup = useCallback(
    (name: string, initialRowIds?: string[]) => {
      const validation = validateGroupName(name);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      const rowIds = initialRowIds || selectedRowIds;

      // Validate that all rowIds exist in availableRows
      const validRowIds = new Set(availableRows.map(row => row.id));
      const filteredRowIds = rowIds.filter(id => validRowIds.has(id));

      const newGroup: Group = {
        id: generateGroupId(),
        name: name.trim(),
        rowIds: [...filteredRowIds],
      };

      setGroups(prev => [...prev, newGroup]);
      setNewGroupName('');

      // Clear selection after creation - no group should be selected
      setActiveGroupId(null);
      setSelectedRowIds([]);

      return newGroup;
    },
    [generateGroupId, validateGroupName, selectedRowIds, availableRows]
  );

  // Update group name
  const updateGroupName = useCallback(
    (groupId: string, name: string) => {
      const validation = validateGroupName(name, groupId);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      setGroups(prev =>
        prev.map(group =>
          group.id === groupId ? { ...group, name: name.trim() } : group
        )
      );
    },
    [validateGroupName]
  );

  // Delete group
  const deleteGroup = useCallback(
    (groupId: string) => {
      setGroups(prev => prev.filter(group => group.id !== groupId));

      // If the deleted group was active, reset to create mode
      if (activeGroupId === groupId) {
        setActiveGroupId(null);
        setNewGroupName('');
        setSelectedRowIds([]);
      }
    },
    [activeGroupId]
  );

  // Update group rows (for modifying existing groups)
  const updateGroupRows = useCallback(
    (groupId: string, newRowIds: string[]) => {
      // Validate that all rowIds exist in availableRows
      const validRowIds = new Set(availableRows.map(row => row.id));
      const filteredRowIds = newRowIds.filter(id => validRowIds.has(id));

      setGroups(prev =>
        prev.map(group =>
          group.id === groupId
            ? { ...group, rowIds: [...filteredRowIds] }
            : group
        )
      );
    },
    [availableRows]
  );

  // Update both group name and rows in one operation
  const updateGroupNameAndRows = useCallback(
    (groupId: string, name: string, newRowIds: string[]) => {
      const validation = validateGroupName(name, groupId);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Validate that all rowIds exist in availableRows
      const validRowIds = new Set(availableRows.map(row => row.id));
      const filteredRowIds = newRowIds.filter(id => validRowIds.has(id));

      setGroups(prev =>
        prev.map(group =>
          group.id === groupId
            ? { ...group, name: name.trim(), rowIds: [...filteredRowIds] }
            : group
        )
      );
    },
    [validateGroupName, availableRows]
  );

  // Assign rows to group
  const assignRowsToGroup = useCallback((groupId: string, rowIds: string[]) => {
    setGroups(prev =>
      prev.map(group => {
        if (group.id === groupId) {
          return { ...group, rowIds };
        }
        // Remove these rows from other groups
        return {
          ...group,
          rowIds: group.rowIds.filter(id => !rowIds.includes(id)),
        };
      })
    );
  }, []);

  // Get group assignments for all rows
  const getGroupAssignments = useCallback((): GroupAssignment[] => {
    return availableRows.map(row => {
      const group = groups.find(g => g.rowIds.includes(row.id));
      return {
        rowId: row.id,
        groupId: group?.id || null,
      };
    });
  }, [availableRows, groups]);

  // Get rows assigned to a specific group
  const getGroupRows = useCallback(
    (groupId: string) => {
      const group = groups.find(g => g.id === groupId);
      if (!group) return [];

      return group.rowIds
        .map(rowId => availableRows.find(row => row.id === rowId))
        .filter(Boolean);
    },
    [groups, availableRows]
  );

  // Get unassigned rows
  const getUnassignedRows = useCallback(() => {
    const assignedRowIds = new Set(groups.flatMap(group => group.rowIds));

    return availableRows.filter(row => !assignedRowIds.has(row.id));
  }, [groups, availableRows]);

  // Filter rows based on search term and group assignment context
  const filteredRows = useMemo(() => {
    // Determine base rows based on context
    let baseRows = availableRows;

    // If no active group, only show unassigned rows (for creating new groups)
    // If active group exists, show unassigned rows PLUS rows from the active group
    if (!activeGroupId) {
      // Creating new group - only show unassigned rows
      const assignedRowIds = new Set(groups.flatMap(group => group.rowIds));
      baseRows = availableRows.filter(row => !assignedRowIds.has(row.id));
    } else {
      // Editing existing group - show unassigned rows + current group's rows
      const otherGroupsRowIds = new Set(
        groups
          .filter(group => group.id !== activeGroupId)
          .flatMap(group => group.rowIds)
      );
      baseRows = availableRows.filter(row => !otherGroupsRowIds.has(row.id));
    }

    // Apply search filter
    let filtered = baseRows;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = baseRows.filter(
        row =>
          row.label.toLowerCase().includes(term) ||
          Object.values(row).some(
            value =>
              typeof value === 'string' && value.toLowerCase().includes(term)
          )
      );
    }

    // Sort: selected items first, then unselected
    const selectedSet = new Set(selectedRowIds);
    return [...filtered].sort((a, b) => {
      const aSelected = selectedSet.has(a.id) ? 0 : 1;
      const bSelected = selectedSet.has(b.id) ? 0 : 1;
      return aSelected - bSelected;
    });
  }, [availableRows, searchTerm, groups, activeGroupId, selectedRowIds]);

  // Create properly formatted rowSelectionModel for DataGrid Premium
  // Only include selections for rows that exist in current filtered view
  // This prevents DataGrid from auto-clearing selections when rows are filtered out
  const getRowIdCallback = useCallback(
    (row: (typeof filteredRows)[number]) => row.id,
    []
  );
  const rowSelectionModel = useFilteredSelectionModel(
    selectedRowIds,
    filteredRows,
    getRowIdCallback
  );

  /**
   * Pure function to calculate reordered groups array
   * @returns New array if valid reorder, null if invalid indices
   */
  const calculateReorderedGroups = (
    groupsArray: Group[],
    activeId: string,
    overId: string
  ): Group[] | null => {
    const oldIndex = groupsArray.findIndex(g => g.id === activeId);
    const newIndex = groupsArray.findIndex(g => g.id === overId);

    if (oldIndex === -1 || newIndex === -1) {
      return null;
    }

    const newGroups = [...groupsArray];
    const [movedGroup] = newGroups.splice(oldIndex, 1);
    newGroups.splice(newIndex, 0, movedGroup);

    return newGroups;
  };

  // Reset form state
  const resetForm = useCallback(() => {
    setActiveGroupId(null);
    setNewGroupName('');
    setSearchTerm('');
    setSelectedRowIds([]);
    // Reset groups back to original state
    setGroups(existingGroups);
  }, [existingGroups]);

  return {
    // State
    groups,
    activeGroupId,
    newGroupName,
    searchTerm,
    filteredRows,
    selectedRowIds,
    rowSelectionModel,

    // Actions
    setGroups,
    setActiveGroupId,
    setNewGroupName,
    setSearchTerm,
    setSelectedRowIds,
    toggleRowSelection,
    clearSelection,
    handleGroupClick,
    createGroup,
    updateGroupName,
    updateGroupRows,
    updateGroupNameAndRows,
    deleteGroup,
    assignRowsToGroup,
    resetForm,

    // Computed
    getGroupAssignments,
    getGroupRows,
    getUnassignedRows,
    validateGroupName,

    // Utilities
    calculateReorderedGroups,
  };
};
